import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getSupabaseUrl, getSupabaseAnonKey } from '../lib/environments';
import { isPlaceholderEmail } from '../utils/phoneDisplayUtils';
import { returnToApp, isInKizuWebView } from '../lib/returnToApp';

interface Plan {
  id: string;
  name: string;
  display_name: string;
  storage_bytes: number;
  max_photos: number;
  max_video_minutes: number;
  // Legacy fields — caps no longer enforced after 2026-04-27 pricing redesign.
  // Backend returns -1 (unlimited); kept here for type compatibility but not displayed.
  max_editors: number;
  max_contributors: number;
  max_read_only_users: number;
  // included_people: how many people slots come with this plan unit
  // (Free=1, Member=1, Family=5, Host=1, Wedding=1, storage_pack=0).
  included_people?: number;
  // Credit grants per renewal — Family/Host get 1 Gathering, Host/Wedding
  // get 1 Event. Free / Member / Storage Pack get 0/0.
  included_gathering_credits_per_year?: number;
  included_event_credits_per_year?: number;
  price_monthly_cents: number;
  price_yearly_cents: number;
  billing_type?: 'subscription' | 'one_time';
  features?: {
    is_addon?: boolean;
    stackable?: boolean;
    pure_storage?: boolean;
    member_label?: string;
    member_label_plural?: string;
    [k: string]: any;
  };
}

interface Subscription {
  id: string;
  status: 'trialing' | 'active' | 'grace_period' | 'read_only' | 'cancelled' | 'expired' | 'free';
  trial_end: string | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id?: string | null;
  has_promo?: boolean;
  applied_promo_code?: string | null;
  applied_promo_plan_id?: string | null;
  payment_provider?: 'apple' | 'stripe' | 'free' | 'promo';
  billing_cycle?: 'monthly' | 'yearly';
}

interface Usage {
  photos: number;
  video_minutes: number;
  storage_bytes: number;
  editors: number;
  contributors: number;
  read_only_users: number;
}

interface Limits {
  max_photos: number;
  max_video_minutes: number;
  max_storage_bytes: number;
  max_editors: number;
  max_contributors: number;
  max_read_only: number;
}

interface PromoResult {
  valid: boolean;
  benefits?: string[];
  error?: string;
  discount_percent?: number;
  trial_days?: number;
  is_perpetual?: boolean;
  // When the promo is restricted to a specific plan, these guide the user
  // (and the Apply Now button) to that plan instead of letting the discount
  // get silently mis-applied to whichever plan they happen to upgrade to.
  plan_id?: string | null;
  plan_name?: string | null;
}

const SUPABASE_URL = getSupabaseUrl();

const handleReturnToApp = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  const inWv = isInKizuWebView();
  console.log('[handleReturnToApp] clicked, inWebView=', inWv);

  // WebView: postMessage(KIZU_CLOSE) → native pops the screen. No URL nav.
  // Plain browser: nothing to do — no path, no scheme, no auto-redirect that
  // could leak to kizu.online (iOS Safari's "open in app" handling for unknown
  // schemes was sending Done to the marketing site).
  if (inWv) {
    returnToApp();
    return;
  }

  // Browser-only fallback: try window.close, alert as last resort.
  setTimeout(() => {
    window.close();
    setTimeout(() => {
      if (!document.hidden) {
        alert('Please return to the Kizu app manually.');
      }
    }, 300);
  }, 500);
};

export const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When the page is rendered inside the Kizu mobile WebView, the native
  // shell already shows a "Manage Account" header with a close button —
  // so the page-level header + bottom Done button are hidden to avoid
  // the duplicate-chrome look.
  const inWebView = isInKizuWebView();

  const [loading, setLoading] = useState(true);
  const [loadingPhase, setLoadingPhase] = useState<'authenticating' | 'loading_account'>('authenticating');
  const [userName, setUserName] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [editorCount, setEditorCount] = useState<number>(0);
  const [addonQuantities, setAddonQuantities] = useState<Record<string, number>>({});
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState<PromoResult | null>(null);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [removingPromo, setRemovingPromo] = useState(false);
  const [systemDiscount, setSystemDiscount] = useState<{
    percent: number;
    label: string;
    description: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    initializeAuth();
  }, []);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccessMessage('Your subscription has been updated. Return to Kizu to continue.');
      loadSubscriptionData();
    }
    // After Stripe checkout, web-subscription.tsx re-loads /subscription with
    // ?checkout=success|cancel — show a banner and refresh the plan state so
    // the user lands back on Manage Account with confirmation, not a blank.
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      // Generic copy because this banner fires for both primary plan
      // changes (Family → Host) AND stackable add-ons (Member, Storage
      // Pack). "Your plan is active" was misleading for add-ons since
      // the primary plan was already active before — what actually
      // happened was a new sub line got added on top.
      setSuccessMessage('Payment successful — subscription updated.');
      loadSubscriptionData();
    } else if (checkout === 'cancel') {
      setError('Checkout cancelled. No charge was made.');
    }
  }, [searchParams]);

  const initializeAuth = async () => {
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');

    // Extract user name from token early if possible
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const earlyName = payload.user_metadata?.full_name || payload.user_metadata?.name || (!isPlaceholderEmail(payload.email) ? payload.email?.split('@')[0] : null) || null;
        if (earlyName) setUserName(earlyName);
      } catch {}
    }

    setLoadingPhase('authenticating');

    if (accessToken && refreshToken) {
      try {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) {
          console.error('[SubscriptionPage] setSession error:', error.message);
          // Try refreshing with just the refresh token
          const { error: refreshError } = await supabase.auth.refreshSession({ refresh_token: refreshToken });
          if (refreshError) {
            console.error('[SubscriptionPage] refreshSession also failed:', refreshError.message);
            setError('Session expired. Please close and try again from the app.');
            setLoading(false);
            return;
          }
        }
        window.history.replaceState({}, '', '/subscription');
      } catch (err) {
        setError('Failed to authenticate. Please try again from the app.');
        setLoading(false);
        return;
      }
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login?redirect=/subscription');
      return;
    }

    // Get user name for loading display
    const userEmail = session.user?.email;
    const userMeta = session.user?.user_metadata;
    const displayName = userMeta?.full_name || userMeta?.name || userEmail?.split('@')[0] || null;
    setUserName(displayName);

    // Switch to loading account phase
    setLoadingPhase('loading_account');

    loadSubscriptionData();
  };

  const loadSubscriptionData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        navigate('/login');
        return;
      }

      const headers = {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        apikey: getSupabaseAnonKey(),
      };

      // Single API call for all page data
      const response = await fetch(`${SUPABASE_URL}/functions/v1/subscription-api?action=page_data`, { headers });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || data.msg || `Server error (${response.status})`);
        return;
      }

      if (data.success) {
        setPlans(data.plans || []);
        setSubscription(data.subscription);
        setCurrentPlan(data.plan);
        setUsage(data.usage);
        setLimits(data.limits);
        setEditorCount(data.editor_count || 0);
        setAddonQuantities(data.addon_quantities || {});
        if (data.discount) {
          setSystemDiscount(data.discount);
        }
      } else {
        setError(data.error || data.msg || 'Failed to load subscription data');
      }
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;

    setPromoValidating(true);
    setPromoResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/subscription-api?action=validate_promo&code=${encodeURIComponent(promoCode.trim())}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      const result = await response.json();

      if (result.valid) {
        setPromoResult({
          valid: true,
          benefits: result.promo.benefits,
          discount_percent: result.promo.discount_percent,
          trial_days: result.promo.trial_days,
          is_perpetual: result.promo.is_perpetual,
          plan_id: result.promo.plan_id || null,
          plan_name: result.promo.plan_name || null,
        });
      } else {
        setPromoResult({ valid: false, error: result.error || 'Invalid promo code' });
      }
    } catch (err) {
      setPromoResult({ valid: false, error: 'Failed to validate promo code' });
    } finally {
      setPromoValidating(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;

    setPromoValidating(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/subscription-api?action=apply_promo`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: promoCode.trim() }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(result.message);
        setPromoCode('');
        setPromoResult(null);
        setPromoModalOpen(false);
        loadSubscriptionData();
      } else {
        setError(result.error || 'Failed to apply promo code');
      }
    } catch (err) {
      setError('Failed to apply promo code');
    } finally {
      setPromoValidating(false);
    }
  };

  const removePromoCode = async () => {
    setRemovingPromo(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/subscription-api?action=remove_promo`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      const result = await response.json();
      if (result.success) {
        setPromoResult(null);
        setPromoCode('');
        loadSubscriptionData();
      } else {
        setError(result.error || 'Failed to remove promo code');
      }
    } catch {
      setError('Failed to remove promo code');
    } finally {
      setRemovingPromo(false);
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    // Clear any stale error/success from a previous attempt so the user
    // doesn't see a banner unrelated to this click.
    setError(null);
    setSuccessMessage(null);
    if (currentPlan?.id === plan.id && subscription?.status !== 'trialing') {
      // Allow re-selecting the same plan if a promo (validated or already
      // applied) targets it — the user wants to (re)buy at the discounted price.
      const isPromoTarget =
        (promoResult?.valid && promoResult.plan_id === plan.id) ||
        (subscription?.applied_promo_plan_id === plan.id);
      if (!isPromoTarget) return;
    }

    // Promo-driven plan change always goes through fresh checkout so the
    // discount actually applies (the portal can't take new promos). Either
    // the just-validated promoResult or the persisted applied promo counts.
    const promoForThisPlan =
      (promoResult?.valid && promoResult.plan_id === plan.id) ||
      (subscription?.applied_promo_plan_id === plan.id);

    // Going to Free: no payment needed; cancel/downgrade the existing sub.
    if (plan.name === 'free') {
      try {
        setCheckoutLoading(plan.id);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/subscription-api?action=downgrade_to_free`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        const result = await response.json();
        if (result.success) {
          setSuccessMessage(result.message || 'Switched to Free');
          loadSubscriptionData();
        } else {
          setError(result.message || result.error || 'Failed to downgrade');
        }
      } catch {
        setError('Failed to downgrade');
      } finally {
        setCheckoutLoading(null);
      }
      return;
    }

    // Active Stripe-managed subscription, no promo override → portal.
    // Stackable add-ons (Member, Storage Pack) are excluded: they create a
    // *new* subscription row on top of the existing one, so they must go
    // through fresh checkout — the portal would only let the user edit
    // their primary sub. One-time plans (Wedding) are also excluded — those
    // are checkout-only purchases, not switchable via the portal.
    const isAddon = plan.features?.is_addon === true;
    const isOneTime = plan.billing_type === 'one_time';
    if (!promoForThisPlan && !isAddon && !isOneTime && isSubscribed && subscription?.stripe_subscription_id) {
      // Pass the target plan + cycle so the backend opens Stripe's portal
      // directly on the "confirm switching to <plan>" screen rather than the
      // generic billing dashboard. UX win: one click + confirm vs. find sub
      // → Update → pick plan → confirm.
      handleManageSubscription({ targetPlanId: plan.id, targetBillingCycle: billingCycle });
      return;
    }

    // Add-on increment path: if the user already owns this stackable add-on
    // (Member, Storage Pack), don't open a fresh checkout. A new checkout
    // would create a second Stripe sub at qty=1, collide with the
    // (user_id, plan_id) DB unique constraint when the webhook lands, and
    // overwrite the row's qty back to 1 — leaving the prior Stripe sub as
    // a silent-billing orphan. Instead bump the existing sub's quantity
    // via subscriptions.update with proration. Stripe charges the prorated
    // delta immediately, the webhook updates the DB row's qty, the seat
    // count grows correctly.
    if (isAddon && (addonQuantities[plan.name] || 0) > 0 && !promoForThisPlan) {
      const monthlyEach = formatPrice(plan.price_monthly_cents);
      const ok = window.confirm(
        `Add another ${plan.display_name}? You'll be charged the prorated amount for the rest of this billing period, then ${monthlyEach}/month going forward per ${plan.display_name}.`,
      );
      if (!ok) return;
      setCheckoutLoading(plan.id);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/subscription-api?action=increment_addon_quantity`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ plan_id: plan.id }),
          },
        );
        const result = await response.json();
        if (result.success) {
          setSuccessMessage(result.message || `Added another ${plan.display_name}.`);
          loadSubscriptionData();
        } else {
          setError(result.message || result.error || 'Could not add another.');
        }
      } catch {
        setError('Could not add another.');
      } finally {
        setCheckoutLoading(null);
      }
      return;
    }

    // All other paths (any paid plan or add-on, with or without a promo) →
    // Stripe checkout. We never silently switch to a paid plan in the DB —
    // payment must be collected.

    // Special case: switching from a recurring primary plan (Host/Family)
    // to a one-time plan (Wedding). The portal subscription_update_confirm
    // flow can't target one-time prices, so this falls through to checkout
    // — but checkout would create a new sub *on top of* the existing one,
    // double-billing the user. Confirm the swap explicitly, then signal
    // the backend to cancel the recurring primary at period end before
    // opening the one-time checkout. User keeps the time they paid for
    // on the recurring side and gets the one-time access immediately.
    let cancelExistingAtPeriodEnd = false;
    if (
      plan.billing_type === 'one_time'
      && isSubscribed
      && subscription?.stripe_subscription_id
      && currentPlan?.billing_type !== 'one_time'
    ) {
      const currentName = currentPlan?.display_name || currentPlan?.name || 'your current plan';
      const ok = window.confirm(
        `Switching to ${plan.display_name} will cancel ${currentName} immediately. ` +
        `You'll receive a prorated credit for the unused portion of your current billing period, ` +
        `applied to the ${formatPrice(plan.price_yearly_cents)} ${plan.display_name} charge. ` +
        `${plan.display_name} access starts right away and lasts one year. Continue?`,
      );
      if (!ok) {
        return;
      }
      cancelExistingAtPeriodEnd = true;
    }

    setCheckoutLoading(plan.id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/stripe-checkout-api?action=create_checkout`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan_id: plan.id,
            // One-time plans have no monthly price; always send 'yearly'
            // so the API picks up stripe_price_id_yearly.
            billing_cycle: plan.billing_type === 'one_time' ? 'yearly' : billingCycle,
            promo_code: promoForThisPlan
              ? (promoResult?.valid ? promoCode.trim() : subscription?.applied_promo_code || undefined)
              : undefined,
            platform: inWebView ? 'mobile' : 'web',
            cancel_existing_at_period_end: cancelExistingAtPeriodEnd,
          }),
        }
      );

      const result = await response.json();

      if (result.error === 'email_required') {
        // Phone-only sign-ups land here. Bounce mobile users into the
        // about-me email-add+verify flow; web users get an inline message.
        if (inWebView && (window as any).ReactNativeWebView) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'KIZU_ADD_EMAIL' })
          );
        } else {
          setError(result.message || 'Please add and verify an email address before purchasing.');
        }
      } else if (result.success && result.checkout_url) {
        // Navigate in-place. In the Kizu WebView, web-subscription.tsx already
        // handles checkout.stripe.com (allow) and kizu(-dev)://… redirects
        // (close screen). Posting KIZU_OPEN_URL would Linking.openURL → Safari,
        // which then triggers the iOS "Open in Kizu?" prompt on the deep link.
        window.location.href = result.checkout_url;
      } else {
        setError(result.error || 'Failed to start checkout');
      }
    } catch (err) {
      setError('Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageSubscription = async (
    target?: { targetPlanId: string; targetBillingCycle: 'monthly' | 'yearly' },
  ) => {
    setPortalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('Session expired. Please close and try again from the app.');
        return;
      }

      // Tell the edge function which platform to use for the portal return URL.
      // In the mobile WebView, we want Stripe's "Return to Kizu" button to fire
      // a kizu:// deep link so the user bounces back into the app rather than
      // landing on a web page in Safari.
      const portalPlatform = isInKizuWebView() ? 'mobile' : 'web';

      const requestBody: any = { platform: portalPlatform };
      if (target) {
        requestBody.target_plan_id = target.targetPlanId;
        requestBody.target_billing_cycle = target.targetBillingCycle;
      }

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/stripe-checkout-api?action=create_portal`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (result.success && result.portal_url) {
        // Always navigate in-place. In the Kizu WebView, web-subscription.tsx
        // intercepts billing.stripe.com (allow-listed) and the kizu(-dev)://
        // return_url (handleClose → goBack to Account). Posting KIZU_OPEN_URL
        // would Linking.openURL → Safari, where Stripe's "Return to Evolve
        // LLC" link triggers the iOS "Open in Kizu?" prompt and often
        // silently fails to bounce back into the app — same issue we already
        // dodged on the checkout path above. The WebView header's X button
        // serves as the user's "Done" affordance.
        window.location.href = result.portal_url;
      } else {
        setError(result.error || 'Failed to open billing portal');
      }
    } catch (err) {
      setError('Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatStorage = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${Math.round(gb)} GB`;
    const mb = bytes / (1024 * 1024);
    return `${Math.round(mb)} MB`;
  };

  // Render usage bar with limit marker when exceeded
  const renderUsageBar = (current: number, max: number, normalColor: string) => {
    const isExceeded = current > max && max > 0;
    const percentage = max > 0 ? (current / max) * 100 : 0;

    if (!isExceeded) {
      // Normal case
      return (
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${current >= max ? 'bg-red-500' : normalColor}`}
            style={{ width: `${Math.min(100, percentage)}%` }}
          />
        </div>
      );
    }

    // Exceeded case: show full bar with limit marker
    // Cap visual at 150% so bar doesn't look too extreme
    const cappedPercentage = Math.min(percentage, 150);
    const limitPosition = (100 / cappedPercentage) * 100;

    return (
      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-visible relative">
        {/* Fill up to limit in normal color */}
        <div
          className={`h-full rounded-l-full ${normalColor}`}
          style={{ width: `${limitPosition}%`, position: 'absolute', left: 0 }}
        />
        {/* Exceeded portion in red */}
        <div
          className="h-full rounded-r-full bg-red-500"
          style={{ width: `${100 - limitPosition}%`, position: 'absolute', left: `${limitPosition}%` }}
        />
        {/* Limit marker line */}
        <div
          className="absolute top-[-2px] w-0.5 h-3 bg-white dark:bg-gray-300 rounded shadow"
          style={{ left: `${limitPosition}%`, transform: 'translateX(-50%)' }}
        />
      </div>
    );
  };

  const isTrialing = subscription?.status === 'trialing';
  const isSubscribed = subscription?.status === 'active';
  const isExpired = subscription?.status === 'expired';
  const isPerpetual = isTrialing && !subscription?.trial_end;
  const hasPromoApplied = subscription?.has_promo || false;

  // Check if user has active Apple subscription (managed via iOS app)
  const hasAppleSubscription = subscription?.payment_provider === 'apple' &&
    ['active', 'trialing'].includes(subscription?.status || '');

  if (loading) {
    const getLoadingMessage = () => {
      if (loadingPhase === 'authenticating') {
        return userName ? `Authenticating ${userName}...` : 'Authenticating...';
      }
      return userName ? `Looking up account information for ${userName}...` : 'Looking up account information...';
    };

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {getLoadingMessage()}
          </p>
        </div>
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-x-hidden">
      {!inWebView && (
        <header className="bg-white dark:bg-gray-800 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Manage Subscription</h1>
            <button
              onClick={handleReturnToApp}
              className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
            >
              Done
            </button>
          </div>
        </header>
      )}

      <main className="max-w-4xl mx-auto px-4 py-4">
        {successMessage && (
          <div className="bg-green-100 dark:bg-green-900/30 border border-green-400 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg mb-6 flex items-center justify-between gap-3 flex-wrap">
            <span>{successMessage}</span>
            {inWebView ? (
              <button
                onClick={() => {
                  if ((window as any).ReactNativeWebView) {
                    (window as any).ReactNativeWebView.postMessage(JSON.stringify({ type: 'KIZU_CLOSE' }));
                  }
                }}
                className="shrink-0 px-3 py-1.5 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-md"
              >
                Done
              </button>
            ) : (
              <button
                onClick={() => setSuccessMessage(null)}
                className="shrink-0 underline text-sm"
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
            <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
          </div>
        )}

        {isExpired && (
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            <h3 className="font-bold text-lg">Subscription Expired</h3>
            <p>Please select a plan to continue using all features.</p>
          </div>
        )}

        {systemDiscount && (
          <div className="bg-emerald-500 text-white p-4 rounded-lg mb-6 flex items-center gap-4">
            <span className="text-3xl">🏷️</span>
            <div className="flex-1">
              <p className="font-bold">{systemDiscount.label}</p>
              <p className="text-sm opacity-90">{systemDiscount.description}</p>
            </div>
            <span className="text-2xl font-bold">{systemDiscount.percent}% OFF</span>
          </div>
        )}

        {/* Apple subscription warning */}
        {hasAppleSubscription && (
          <div className="bg-amber-100 dark:bg-amber-900/30 border border-amber-400 p-4 rounded-lg mb-6">
            <div className="flex items-start gap-4">
              <span className="text-2xl">📱</span>
              <div className="flex-1">
                <h3 className="font-bold text-amber-800 dark:text-amber-200">
                  Subscription Active via App Store
                </h3>
                <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                  Your {currentPlan?.display_name || 'subscription'} is managed through Apple.
                  To make changes, open the Kizu app on your iPhone or go to your Apple ID subscription settings.
                </p>
                <a
                  href="https://support.apple.com/en-us/HT202039"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition"
                >
                  How to manage Apple subscriptions
                  <span>↗</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Combined Plan + Usage card — single-line summary, bars below */}
        {(() => {
          // Prefer the backend-aggregated value (primary plan + Member add-on
          // contribution). Fall back to the primary plan's static field for
          // older API responses that don't include max_sponsored_members.
          const seats = (limits as any)?.max_sponsored_members
            ?? (currentPlan ? (currentPlan.included_people || 1) - 1 : 0);
          const showStatusPill = isSubscribed || isTrialing || isExpired;
          const showProviderTag = subscription?.payment_provider && ['apple', 'stripe'].includes(subscription.payment_provider) && (isSubscribed || isTrialing);
          const renewalText =
            isTrialing && !isPerpetual && subscription?.trial_end
              ? `Trial ends ${new Date(subscription.trial_end).toLocaleDateString()}`
              : isSubscribed && subscription?.current_period_end
                ? `${subscription.has_promo ? 'Expires' : 'Renews'} ${new Date(subscription.current_period_end).toLocaleDateString()}`
                : null;

          return (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 mb-3">
              {/* Single line: plan · cycle · storage · members · renewal | status */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                  <h2 className="text-base font-semibold text-indigo-600 dark:text-indigo-400 leading-none">
                    {currentPlan?.display_name || 'Free'}
                  </h2>
                  {subscription?.billing_cycle && (isSubscribed || isTrialing) && (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      · {subscription.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                    </span>
                  )}
                  {usage && limits && (
                    <span className={`text-[11px] tabular-nums ${usage.storage_bytes > limits.max_storage_bytes ? 'text-red-500 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                      · {formatStorage(usage.storage_bytes)} / {formatStorage(limits.max_storage_bytes)}
                    </span>
                  )}
                  {seats > 0 && (
                    <span className={`text-[11px] tabular-nums ${editorCount > seats ? 'text-red-500 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                      · {editorCount}/{seats} members
                    </span>
                  )}
                  {renewalText && (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">· {renewalText}</span>
                  )}
                </div>
                {(showStatusPill || showProviderTag) && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {showStatusPill && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold text-white ${
                        isSubscribed ? 'bg-green-500' :
                        isTrialing ? (isPerpetual ? 'bg-green-500' : 'bg-orange-500') :
                        'bg-red-500'
                      }`}>
                        {isSubscribed ? 'Active' :
                         isTrialing ? (isPerpetual ? '∞' : `${subscription?.trial_end ? Math.ceil((new Date(subscription.trial_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 0}d`) :
                         'Expired'}
                      </span>
                    )}
                    {showProviderTag && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        subscription!.payment_provider === 'apple'
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      }`}>
                        {subscription!.payment_provider === 'apple' ? '🍎' : '💳'}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Thin progress bars for visual confirmation */}
              {usage && limits && (
                <div className="space-y-1 mt-2">
                  {renderUsageBar(usage.storage_bytes, limits.max_storage_bytes, 'bg-emerald-500')}
                  {seats > 0 && renderUsageBar(editorCount, seats, 'bg-indigo-500')}
                </div>
              )}
            </div>
          );
        })()}

        {/* Sticky row: billing cycle toggle + promo code button (modal) */}
        {!hasAppleSubscription && (
          <div className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 py-2.5 -mx-4 px-4 flex items-center justify-between gap-2 flex-wrap">
            <div className="inline-flex bg-gray-200 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  billingCycle === 'monthly'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  billingCycle === 'yearly'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Yearly
              </button>
            </div>
            <button
              onClick={() => { setPromoCode(''); setPromoResult(null); setPromoModalOpen(true); }}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition ${
                hasPromoApplied
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-mono'
                  : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
              }`}
            >
              {hasPromoApplied ? `✓ ${subscription?.applied_promo_code}` : 'Promo code'}
            </button>
          </div>
        )}

        {/* Inline promo expansion — same card style as the rest of the page,
            no fixed-positioning modal (which fights the in-app webview). */}
        {!hasAppleSubscription && promoModalOpen && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 mt-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Promo code</h3>
              <button
                onClick={() => setPromoModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none px-1"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {hasPromoApplied && (
              <div className="mb-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Applied</p>
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-md font-mono text-xs">
                  {subscription?.applied_promo_code}
                  <button
                    onClick={removePromoCode}
                    disabled={removingPromo}
                    className="ml-0.5 text-green-700 dark:text-green-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 leading-none"
                    aria-label="Remove promo"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                autoFocus
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && promoCode.trim()) validatePromoCode(); }}
                placeholder="Enter code"
                style={{ fontSize: '16px' }}
                className="flex-1 min-w-0 basis-[140px] px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
              <button
                onClick={validatePromoCode}
                disabled={promoValidating || !promoCode.trim()}
                className="shrink-0 px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50 inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
              >
                {promoValidating ? (
                  <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                ) : 'Check'}
              </button>
            </div>

            {promoResult && (
              <div className={`mt-2 p-2 rounded-md ${promoResult.valid ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                {promoResult.valid ? (
                  <>
                    <p className="text-green-700 dark:text-green-400 font-medium text-xs">
                      ✓ Valid{promoResult.plan_name ? ` — applies to ${promoResult.plan_name} only` : ''}
                    </p>
                    {promoResult.benefits?.map((benefit, i) => (
                      <p key={i} className="text-[11px] text-gray-700 dark:text-gray-300 mt-0.5">{benefit}</p>
                    ))}
                    <button
                      onClick={applyPromoCode}
                      disabled={promoValidating}
                      className="mt-2 w-full px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                    >
                      {promoValidating ? (
                        <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      ) : 'Apply'}
                    </button>
                  </>
                ) : (
                  <p className="text-red-700 dark:text-red-400 text-xs">✗ {promoResult.error}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Plan list - only show for non-Apple subscribers.
            Skip the user's current plan — it's already shown in the summary card.
            Exception: if a validated promo targets the user's current plan,
            keep that plan visible so they can buy/swap with the new promo. */}
        {!hasAppleSubscription && (
          <div className="space-y-2 mt-3">
            {plans.filter(p => {
              const isCurrent = currentPlan?.id === p.id && p.features?.is_addon !== true;
              const isPromoTarget = promoResult?.valid && promoResult.plan_id === p.id;
              return !isCurrent || isPromoTarget;
            }).map((plan) => {
              const isOneTime = plan.billing_type === 'one_time';
              // One-time plans (Wedding) only have a yearly price; ignore the
              // monthly/yearly toggle for those.
              const price = isOneTime
                ? plan.price_yearly_cents
                : (billingCycle === 'yearly' ? plan.price_yearly_cents : plan.price_monthly_cents);
              const currentPrice = currentPlan
                ? (currentPlan.billing_type === 'one_time'
                    ? currentPlan.price_yearly_cents
                    : (billingCycle === 'yearly' ? currentPlan.price_yearly_cents : currentPlan.price_monthly_cents))
                : 0;
              const isAddon = plan.features?.is_addon === true;
              const addonQty = addonQuantities[plan.name] || 0;
              // Normalize to annualized cents for the up/downgrade comparison.
              // The display `price`/`currentPrice` above use the cycle the
              // user has toggled, which mixes units when one side is a
              // one-time plan: e.g. Wedding $79.99/yr vs Host $9.99/mo
              // would compare 7999 vs 999 and mis-label Host as a Downgrade
              // even though Host costs more per year ($119.88 vs $79.99).
              const annualCents = (p: Plan): number =>
                p.price_yearly_cents > 0
                  ? p.price_yearly_cents
                  : p.price_monthly_cents * 12;
              const isDowngrade = !isAddon && currentPlan
                && annualCents(plan) < annualCents(currentPlan);
              const discountedPrice = systemDiscount && price > 0
                ? Math.round(price * (1 - systemDiscount.percent / 100))
                : price;
              const isCheckingOut = checkoutLoading === plan.id;
              const isFree = plan.name === 'free';

              const getButtonClass = () => {
                if (isDowngrade) return 'bg-orange-500 hover:bg-orange-600 text-white';
                return 'bg-indigo-600 hover:bg-indigo-700 text-white';
              };

              const addonExtraCents = addonQty * (billingCycle === 'yearly' ? plan.price_yearly_cents : plan.price_monthly_cents);

              const showMembers = !isAddon && !isFree;
              const gatherings = plan.included_gathering_credits_per_year ?? 0;
              const events = plan.included_event_credits_per_year ?? 0;
              const specs = [
                formatStorage(plan.storage_bytes),
                showMembers ? `${(plan.included_people || 1).toLocaleString()} ${(plan.included_people || 1) === 1 ? 'member' : 'members'}` : null,
                gatherings > 0 ? `${gatherings} gathering${gatherings === 1 ? '' : 's'}` : null,
                events > 0 ? `${events} event${events === 1 ? '' : 's'}` : null,
              ].filter(Boolean) as string[];

              const priceSuffix = discountedPrice > 0
                ? (isOneTime ? ' once' : `/${billingCycle === 'yearly' ? 'yr' : 'mo'}${isAddon ? ' ea' : ''}`)
                : '';

              // Member is flagged is_addon but doubles as the entry-level
              // primary plan: a Free user picking it is upgrading, not
              // adding to anything. Only call it "Add" once they actually
              // have a paid primary to attach to.
              const hasPaidSub = isSubscribed && currentPlan?.name !== 'free';
              const verb = isDowngrade ? 'Downgrade' : (isAddon && hasPaidSub ? 'Add' : 'Upgrade');
              const showSpinner = isCheckingOut || (portalLoading && isSubscribed && subscription?.stripe_subscription_id);

              const isPromoTarget =
                (promoResult?.valid && promoResult.plan_id === plan.id) ||
                (subscription?.applied_promo_plan_id === plan.id);
              return (
                <div
                  key={plan.id}
                  id={`plan-card-${plan.id}`}
                  className={`bg-white dark:bg-gray-800 rounded-xl p-3 border ${isPromoTarget ? 'border-green-500 ring-2 ring-green-500/30' : 'border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Left: name — specs, takes remaining space */}
                    <div className="min-w-0 flex-1 basis-[140px] flex items-baseline gap-1.5 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {plan.display_name}
                      </h3>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 break-words">
                        {specs.join(' · ')}
                      </span>
                    </div>

                    {/* Middle: price */}
                    {discountedPrice > 0 && (
                      <span className="shrink-0 text-xs text-gray-700 dark:text-gray-300 tabular-nums whitespace-nowrap">
                        <span className="font-semibold">{formatPrice(discountedPrice)}</span>
                        <span className="text-gray-500 dark:text-gray-400">{priceSuffix}</span>
                      </span>
                    )}

                    {/* Right: action button */}
                    <button
                      onClick={() => handleSelectPlan(plan)}
                      disabled={isCheckingOut || portalLoading || (isFree && !isDowngrade)}
                      className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-md transition disabled:opacity-50 whitespace-nowrap inline-flex items-center justify-center gap-1.5 ${getButtonClass()}`}
                    >
                      {showSpinner ? (
                        <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      ) : (
                        verb
                      )}
                    </button>
                  </div>

                  {isAddon && addonQty > 0 && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                      You have {addonQty} · {formatPrice(addonExtraCents)}/{billingCycle === 'yearly' ? 'yr' : 'mo'}
                    </p>
                  )}

                  {isDowngrade && !isAddon && (
                    <p className="text-[11px] text-orange-600 dark:text-orange-400 mt-1.5 leading-snug">
                      Keeps current features until {subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'period ends'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Manage Billing only makes sense for recurring Stripe subscriptions —
            one-time plans (Wedding) and promo-only entitlements have no
            invoice or sub for the portal to show, so it'd open empty. */}
        {isSubscribed && subscription?.stripe_subscription_id && (
          <div className="mt-4">
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="w-full py-2.5 text-sm border border-indigo-600 text-indigo-600 dark:text-indigo-400 rounded-lg font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {portalLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                  Opening billing portal...
                </>
              ) : (
                '💳 Manage Billing & Payment'
              )}
            </button>
          </div>
        )}

      </main>
    </div>
  );
};

export default SubscriptionPage;
